import React, { useEffect, useState } from "react";
import { History, Filter } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const ACTION_OPTIONS = [
  "all",
  "self_password_change",
  "force_password_change",
  "invite_create",
  "invite_revoke",
  "invite_accepted",
  "admin_update",
  "admin_revoke",
  "platform_settings_update",
];

/**
 * Activity Logs panel.
 * - Owner sees every admin action across the platform.
 * - Admins see ONLY their own actions (backend enforces).
 */
export default function AdminActivityPanel() {
  const { isOwner } = useAuth();
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { action: filter } : {};
      const { data } = await api.get("/admin/activity", { params });
      setLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      setLogs([]);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="admin-activity-panel" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold flex items-center gap-1.5">
            <History size={12}/> Activity audit log
          </div>
          <div className="text-sm text-[#071A2D]/70 mt-0.5">
            {isOwner()
              ? "Append-only — every Owner & admin action is recorded permanently."
              : "You are viewing your own actions. The Owner can see all admin activity."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={12} className="text-[#064E3B]"/>
          <select
            data-testid="activity-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-xs border border-[#071A2D]/15 rounded-full px-3 py-1.5 bg-white"
          >
            {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        {loading && (
          <div className="p-8 text-center text-[#071A2D]/60">Loading…</div>
        )}
        {!loading && logs.length === 0 && (
          <div data-testid="activity-empty" className="p-8 text-center text-[#071A2D]/60 rounded-2xl bg-[#FFF8EE] border border-dashed border-[#071A2D]/15">
            No activity recorded for this filter yet.
          </div>
        )}
        {logs.map((l, idx) => (
          <div
            key={l.log_id || idx}
            data-testid={`activity-row-${idx}`}
            className="p-4 rounded-2xl card-luxury"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="font-medium text-sm">{l.action?.replace(/_/g, " ")}</div>
                <div className="text-xs text-[#071A2D]/60 mt-0.5">
                  {l.actor_email || l.actor_user_id} · {l.actor_role || "admin"}
                </div>
              </div>
              {l.target_type && (
                <div className="text-[10px] uppercase tracking-widest text-[#064E3B]/70">
                  {l.target_type} · {l.target_id}
                </div>
              )}
              <div className="text-[10px] text-[#071A2D]/55 font-mono">
                {l.at?.replace("T", " ").slice(0, 19)}
              </div>
            </div>
            {l.meta && Object.keys(l.meta || {}).length > 0 && (
              <pre className="mt-2 text-[11px] text-[#071A2D]/70 bg-white/70 rounded-lg p-2 overflow-x-auto border border-[#071A2D]/8">
                {JSON.stringify(l.meta, null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
